import * as THREE from 'three';
import { point3d, rad2deg, point2d, LWPolylineFlags, SplineFlags } from '@tarikjabiri/dxf';
import GeometryProcessor from '../../utils/exports/GeometryProcessor';
import { convertBoundaryPaths, createHatchFill, createSolidHatchFromPoints, MIN_BOUNDARY_POINTS } from './hatchUtils';

/**
 * Creates a common options object for DXF entities.
 * @param {object} options - The user data associated with the entity.
 * @param {string} options.layerName - The name of the layer for the entity.
 * @param {number} [options.colorNumber] - The optional color number for the entity.
 * @returns {object} An object containing common DXF properties (layerName, and optionally colorNumber).
 */
const getCommonDxfOptions = ({ layerName, colorNumber }) => ({
  layerName,
  ...(colorNumber !== undefined ? { colorNumber } : {}),
});

/**
 * A shared instance of GeometryProcessor for handling geometric transformations across all entity handlers.
 * This avoids creating a new processor for every entity.
 * @type {GeometryProcessor}
 */
const sharedGeometryProcessor = new GeometryProcessor();

/**
 * Handles the creation of a LWPOLYLINE entity.
 * It extracts geometry from a Three.js object and adds it as a polyline to the DXF writer.
 * This handler is used for both 'polyline' and 'rect' entity types.
 *
 * @param {object} writer - The DXFWriter instance.
 * @param {object} userData - The user data for the polyline.
 * @param {THREE.Object3D} userData.threeObject - The Three.js object containing the geometry.
 * @param {boolean} [userData.closed] - Whether the polyline is closed.
 * @param {boolean} [isClosed] - An optional override to force the polyline's closed state.
 */
const handleLwPolyline = (writer, userData, isClosed) => {
  const { threeObject } = userData;

  try {
    if (!threeObject) {
      console.warn('Skipping polyline/rect: threeObject is missing.');
      return;
    }

    const extractedData = sharedGeometryProcessor.extractPolylineData(threeObject);
    if (!extractedData || !extractedData.points) {
      console.warn('Skipping polyline/rect: Could not extract geometry.');
      return;
    }

    const finalPoints = extractedData.points.map(p => ({
      x: p.x,
      y: p.y
    }));

    if (finalPoints.length < 2) {
      console.warn('Skipping polyline/rect: Insufficient valid points');
      return;
    }

    const vertices = finalPoints.map(p => ({ point: point2d(p.x, p.y) }));

    const isPolylineClosed = isClosed !== undefined ? isClosed : (userData.closed || extractedData.closed || false);

    const dxfOptions = {
      ...getCommonDxfOptions(userData),
      ...(isPolylineClosed && { flags: LWPolylineFlags.Closed }),
    };

    writer.document.modelSpace.addLWPolyline(vertices, dxfOptions);

  } catch (error) {
    console.error('Error processing polyline/rect:', error);
  }
};



/**
 * A map of entity types to their handler functions for DXF export.
 * Each handler processes a specific geometry type from the scene
 * and adds the corresponding entity to the DXF document.
 *
 * Each handler receives the DXF writer and the entity's `userData`.
 *
 * @type {Object.<string, function(object, object): void>}
 */
const entityHandlers = {
  /**
   * Processes a 'line' entity by extracting its vertices directly from the geometry.
   * If the line has more than 2 points, it exports as a polyline instead.
   * @param {object} writer - The DXF writer instance.
   * @param {object} userData - Entity-specific data.
   * @param {THREE.Object3D} userData.threeObject - The Three.js object containing the line's geometry.
   */
  line: (writer, userData) => {
    const { threeObject } = userData;

    if (!threeObject || !threeObject.geometry?.attributes?.position) {
      console.warn('Skipping line with invalid geometry:', userData);
      return;
    }

    const positions = threeObject.geometry.attributes.position;
    const vertexCount = positions.count;

    if (vertexCount < 2) {
      console.warn('Skipping line with insufficient vertices:', userData);
      return;
    }

    const { matrix } = sharedGeometryProcessor.getTransformation(threeObject);

    // Check if this is a LineLoop (closed polyline)
    const isLineLoop = threeObject.type === 'LineLoop';

    // If line has more than 2 vertices, export as polyline to preserve all segments
    if (vertexCount > 2 || isLineLoop) {
      const vertices = [];
      for (let i = 0; i < vertexCount; i++) {
        const point = {
          x: positions.getX(i),
          y: positions.getY(i),
          z: positions.getZ ? positions.getZ(i) : 0
        };
        const transformed = sharedGeometryProcessor.transformPoint(point, matrix);
        vertices.push({ point: point2d(transformed.x, transformed.y) });
      }

      const polylineOptions = {
        ...getCommonDxfOptions(userData),
        ...(isLineLoop && { flags: LWPolylineFlags.Closed })
      };

      writer.document.modelSpace.addLWPolyline(vertices, polylineOptions);
    } else {
      // Simple line with 2 vertices
      const start = { x: positions.getX(0), y: positions.getY(0), z: positions.getZ ? positions.getZ(0) : 0 };
      const end = { x: positions.getX(1), y: positions.getY(1), z: positions.getZ ? positions.getZ(1) : 0 };

      const newStart = sharedGeometryProcessor.transformPoint(start, matrix);
      const newEnd = sharedGeometryProcessor.transformPoint(end, matrix);

      writer.document.modelSpace.addLine(
        point3d(newStart.x, newStart.y, 0),
        point3d(newEnd.x, newEnd.y, 0),
        getCommonDxfOptions(userData)
      );
    }
  },

  /**
   * Processes a 'circle' entity.
   * @param {object} writer - The DXF writer instance.
   * @param {object} userData - Entity-specific data.
   * @param {THREE.Object3D} userData.threeObject - The Three.js object for transformation.
   * @param {{x: number, y: number}} userData.center - The center point of the circle.
   * @param {number} userData.radius - The radius of the circle.
   */
  circle: (writer, userData) => {
    const { center, radius, threeObject } = userData;
    if (center && radius !== undefined && center.x !== undefined && center.y !== undefined) {
      const { matrix } = sharedGeometryProcessor.getTransformation(threeObject);
      const newCenter = sharedGeometryProcessor.transformPoint(center, matrix);

      // Apply world scale to radius
      let scaledRadius = radius;
      if (threeObject) {
        const worldScale = new THREE.Vector3();
        threeObject.getWorldScale(worldScale);
        scaledRadius = radius * worldScale.x; // Assume uniform scale for circles
      }

      writer.document.modelSpace.addCircle(point3d(newCenter.x, newCenter.y, 0), scaledRadius, getCommonDxfOptions(userData));
    } else {
      console.warn('Skipping circle with invalid data:', { center, radius });
    }
  },

  /**
   * Processes an 'arc' entity.
   * @param {object} writer - The DXF writer instance.
   * @param {object} userData - Entity-specific data.
   * @param {THREE.Object3D} userData.threeObject - The Three.js object for transformation.
   * @param {{x: number, y: number}} userData.center - The center point of the arc.
   * @param {number} userData.radius - The radius of the arc.
   * @param {number} userData.startAngle - The start angle in radians.
   * @param {number} userData.endAngle - The end angle in radians.
   */
  arc: (writer, userData) => {
    const { center, radius, startAngle, endAngle, threeObject } = userData;
    if (center && radius !== undefined && startAngle !== undefined && endAngle !== undefined) {
      const { matrix, rotationZ } = sharedGeometryProcessor.getTransformation(threeObject);
      const newCenter = sharedGeometryProcessor.transformPoint(center, matrix);

      // Apply world scale to radius
      let scaledRadius = radius;
      if (threeObject) {
        const worldScale = new THREE.Vector3();
        threeObject.getWorldScale(worldScale);
        scaledRadius = radius * worldScale.x; // Assume uniform scale for arcs
      }

      const newStartAngle = startAngle + rotationZ;
      const newEndAngle = endAngle + rotationZ;

      writer.document.modelSpace.addArc(point3d(newCenter.x, newCenter.y, 0), scaledRadius, rad2deg(newStartAngle), rad2deg(newEndAngle), getCommonDxfOptions(userData));
    } else {
      console.warn('Skipping arc with invalid data:', { center, radius, startAngle, endAngle });
    }
  },

  /**
   * Processes an 'ellipse' entity.
   * @param {object} writer - The DXF writer instance.
   * @param {object} userData - Entity-specific data.
   * @param {THREE.Object3D} userData.threeObject - The Three.js object for transformation.
   * @param {{x: number, y: number}} userData.center - The center point of the ellipse.
   * @param {number} userData.radiusX - The horizontal radius (major axis).
   * @param {number} userData.radiusY - The vertical radius (minor axis).
   * @param {number} userData.rotation - The rotation of the ellipse in radians.
   */
  ellipse: (writer, userData) => {
    const { center, radiusX, radiusY, rotation, threeObject } = userData;
    if (center && radiusX !== undefined && radiusY !== undefined && rotation !== undefined) {
      const { matrix, rotationZ } = sharedGeometryProcessor.getTransformation(threeObject);
      const newCenter = sharedGeometryProcessor.transformPoint(center, matrix);

      // Apply world scale to radii
      let scaledRadiusX = radiusX;
      let scaledRadiusY = radiusY;
      if (threeObject) {
        const worldScale = new THREE.Vector3();
        threeObject.getWorldScale(worldScale);
        scaledRadiusX = radiusX * worldScale.x;
        scaledRadiusY = radiusY * worldScale.y;
      }

      const newRotation = rotation + rotationZ;
      const { x: majorAxisX, y: majorAxisY } = sharedGeometryProcessor.calculateMajorAxisEndpoint(scaledRadiusX, newRotation);
      const endPoint = point3d(majorAxisX, majorAxisY, 0);
      writer.document.modelSpace.addEllipse(point3d(newCenter.x, newCenter.y, 0), endPoint, scaledRadiusY / scaledRadiusX, 0, 2 * Math.PI, getCommonDxfOptions(userData));
    } else {
      console.warn('Skipping ellipse with invalid data:', { center, radiusX, radiusY, rotation });
    }
  },

  /**
   * Processes a 'polyline' entity by delegating to the `handleLwPolyline` function.
   * @param {object} writer - The DXF writer instance.
   * @param {object} userData - The user data for the polyline entity.
   */
  polyline: (writer, userData) => {
    handleLwPolyline(writer, userData);
  },

  /**
   * Processes a 'spline' entity. Exports as a true SPLINE if it has 4 or more control points,
   * otherwise falls back to an LWPOLYLINE using the spline's interpolated points for a smooth approximation.
   * @param {object} writer - The DXF writer instance.
   * @param {object} userData - Entity-specific data.
   * @param {THREE.Object3D} [userData.threeObject] - The Three.js object for transformation.
   * @param {Array<{x: number, y: number, z: number}>} [userData.controlPoints] - The control points defining the spline.
   * @param {Array<{x: number, y: number, z: number}>} [userData.splinePoints] - The interpolated points for the polyline fallback.
   * @param {boolean} [userData.closed] - Whether the spline is closed.
   */
  spline: (writer, userData) => {
    const { controlPoints, splinePoints, threeObject, closed } = userData;

    if (controlPoints && Array.isArray(controlPoints) && controlPoints.length >= 4) {
      let finalPoints = controlPoints;

      if (threeObject) {
        const { matrix } = sharedGeometryProcessor.getTransformation(threeObject);
        finalPoints = sharedGeometryProcessor.transformPoints(controlPoints, matrix);
      }

      const validPoints = sharedGeometryProcessor.filterValidPoints(finalPoints);
      
      if (validPoints.length >= 4) {
        writer.document.modelSpace.addSpline({
          controlPoints: validPoints.map(p => point3d(p.x, p.y, p.z || 0)),
          flags: closed ? SplineFlags.Closed : SplineFlags.None,
          ...getCommonDxfOptions(userData)
        });
        return;
      }
    }
    
    if (splinePoints && Array.isArray(splinePoints) && splinePoints.length > 1) {
      let finalPoints = splinePoints;

      if (threeObject) {
        const { matrix } = sharedGeometryProcessor.getTransformation(threeObject);
        finalPoints = sharedGeometryProcessor.transformPoints(splinePoints, matrix);
      }

      const validPoints = sharedGeometryProcessor.filterValidPoints(finalPoints);
      const vertices = validPoints.map(p => ({ point: point2d(p.x, p.y) }));

      if (vertices.length > 1) {
        writer.document.modelSpace.addLWPolyline(vertices, getCommonDxfOptions(userData));
      }
    } else {
      console.warn('Skipping spline with invalid data:', { controlPoints, splinePoints });
    }
  },

  /**
   * Processes a 'text' entity.
   * @param {object} writer - The DXF writer instance.
   * @param {object} userData - Entity-specific data.
   * @param {THREE.Object3D} userData.threeObject - The Three.js object for transformation.
   * @param {string} userData.text - The text content.
   * @param {number} userData.fontSize - The font size.
   */
  text: (writer, userData) => {
    const { text, fontSize, threeObject } = userData;
    if (text && fontSize !== undefined && threeObject) {
      const { position, rotationZ } = sharedGeometryProcessor.getTransformation(threeObject);

      const textEntity = writer.document.modelSpace.addText(point3d(position.x, position.y, 0), fontSize, text, {
        rotation: rad2deg(rotationZ) || 0,
        ...getCommonDxfOptions(userData)
      });
      textEntity.textStyle = 'Arial';
    } else {
      console.warn('Skipping text with invalid data:', { text, position: userData.position, fontSize });
    }
  },

  /**
   * Processes a multi-line 'mtext' entity.
   * @param {object} writer - The DXF writer instance.
   * @param {object} userData - Entity-specific data, including MTEXT properties.
   * @param {THREE.Object3D} userData.threeObject - The Three.js object for transformation.
   * @param {string} userData.rawText - The raw text content.
   * @param {number} userData.baseFontSize - The base font size.
   * @param {number} [userData.width] - The width of the MTEXT box.
   */
  mtext: (writer, userData) => {
    const { rawText, baseFontSize, width, threeObject } = userData;

    if (rawText && baseFontSize !== undefined && threeObject) {
      try {
        const { position } = sharedGeometryProcessor.getTransformation(threeObject);
        const finalPosition = point3d(position.x, position.y, position.z);

        const finalAttachment = userData.attachmentPoint || 1;
        const finalRotation = userData.rotation || 0;
        const finalDirection = userData.direction;
        const finalLineSpacing = userData.lineSpacingStyle;

        const mtextEntity = writer.document.modelSpace.addMText(finalPosition, baseFontSize, rawText, {
          width: width || 0,
          attachmentPoint: finalAttachment,
          rotation: finalRotation,
          drawingDirection: finalDirection,
          lineSpacingStyle: finalLineSpacing,
          ...getCommonDxfOptions(userData),
        });
        mtextEntity.textStyle = 'Arial';
      } catch (error) {
        console.error('Error processing MTEXT for DXF export:', error);
      }
    } else {
      console.warn('Skipping mtext with invalid data:', { rawText, position: userData.position, baseFontSize });
    }
  },



  /**
   * Processes an 'ellipticArc' entity.
   * @param {object} writer - The DXF writer instance.
   * @param {object} userData - Entity-specific data.
   * @param {THREE.Object3D} userData.threeObject - The Three.js object for transformation.
   * @param {{x: number, y: number}} userData.center - The center point of the ellipse.
   * @param {number} userData.majorRadius - The major radius of the ellipse.
   * @param {number} userData.minorRadius - The minor radius of the ellipse.
   * @param {number} userData.rotation - The rotation of the ellipse in radians.
   * @param {number} userData.startAngle - The start angle of the arc in radians.
   * @param {number} userData.endAngle - The end angle of the arc in radians.
   */
  ellipticArc: (writer, userData) => {
    const { center, majorRadius, minorRadius, rotation, startAngle, endAngle, threeObject } = userData;
    if (center && majorRadius !== undefined && minorRadius !== undefined && rotation !== undefined && startAngle !== undefined && endAngle !== undefined) {
      // Transform center if threeObject is available
      let finalCenter = center;
      let finalRotation = rotation;
      let scaledMajorRadius = majorRadius;
      let scaledMinorRadius = minorRadius;

      if (threeObject) {
        const { matrix, rotationZ } = sharedGeometryProcessor.getTransformation(threeObject);
        finalCenter = sharedGeometryProcessor.transformPoint(center, matrix);
        finalRotation = rotation + rotationZ;

        // Apply world scale to radii
        const worldScale = new THREE.Vector3();
        threeObject.getWorldScale(worldScale);
        scaledMajorRadius = majorRadius * worldScale.x;
        scaledMinorRadius = minorRadius * worldScale.y;
      }

      const { x: majorAxisX, y: majorAxisY } = sharedGeometryProcessor.calculateMajorAxisEndpoint(scaledMajorRadius, finalRotation);
      const endPoint = point3d(majorAxisX, majorAxisY, 0);
      writer.document.modelSpace.addEllipse(point3d(finalCenter.x, finalCenter.y, 0), endPoint, scaledMinorRadius / scaledMajorRadius, startAngle, endAngle, getCommonDxfOptions(userData));
    } else {
      console.warn('Skipping ellipticArc with invalid data:', { center, majorRadius, minorRadius, rotation, startAngle, endAngle });
    }
  },

  /**
   * Processes a 'rect' entity by delegating to `handleLwPolyline` with `isClosed` set to true.
   * @param {object} writer - The DXF writer instance.
   * @param {object} userData - The user data for the rectangle entity.
   */
  rect: (writer, userData) => {
    handleLwPolyline(writer, userData, true);
  },

  /**
   * Processes a 'hatch' entity using modern DXF library API
   * @param {object} writer - The DXF writer instance.
   * @param {object} userData - Entity data containing the boundary paths and pattern info.
   */
  hatch: (writer, userData) => {
    const { boundaryPaths, threeObject } = userData;

    if (!boundaryPaths?.length) {
      console.warn('Skipping hatch with invalid or missing boundary paths');
      return;
    }

    try {
      // Apply transformation to boundary paths
      let transformedBoundaryPaths = boundaryPaths;
      if (threeObject) {
        const { matrix } = sharedGeometryProcessor.getTransformation(threeObject);
        transformedBoundaryPaths = boundaryPaths.map(boundary => {
          // boundary = { type: 'outer'|'inner', groups: [...] }
          if (!boundary || !boundary.groups) return boundary;

          return {
            ...boundary,
            groups: boundary.groups.map(group => {
              // group can be array or object with edgeType
              if (Array.isArray(group)) {
                // Array of edges - transform each based on edge type
                return group.map(edge => {
                  if (!edge) return edge;

                  // Handle Line edge format: x1, y1, x2, y2
                  if (edge.edgeType === 'Line' && typeof edge.x1 === 'number') {
                    const p1 = sharedGeometryProcessor.transformPoint({ x: edge.x1, y: edge.y1, z: 0 }, matrix);
                    const p2 = sharedGeometryProcessor.transformPoint({ x: edge.x2, y: edge.y2, z: 0 }, matrix);
                    return { ...edge, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
                  }

                  // Handle CircularArc edge format: x, y, radius
                  if (edge.edgeType === 'CircularArc' && typeof edge.x === 'number') {
                    const center = sharedGeometryProcessor.transformPoint({ x: edge.x, y: edge.y, z: 0 }, matrix);
                    // Note: radius might need scaling if matrix has scale
                    return { ...edge, x: center.x, y: center.y };
                  }

                  // Handle simple vertex format: { x, y, z }
                  if (typeof edge.x === 'number' && typeof edge.y === 'number' && !edge.edgeType) {
                    return sharedGeometryProcessor.transformPoint(edge, matrix);
                  }

                  return edge;
                });
              } else if (group && typeof group === 'object') {
                // Single edge object

                // Handle Line edge format: x1, y1, x2, y2
                if (group.edgeType === 'Line' && typeof group.x1 === 'number') {
                  const p1 = sharedGeometryProcessor.transformPoint({ x: group.x1, y: group.y1, z: 0 }, matrix);
                  const p2 = sharedGeometryProcessor.transformPoint({ x: group.x2, y: group.y2, z: 0 }, matrix);
                  return { ...group, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
                }

                // Handle CircularArc edge format: x, y, radius
                if (group.edgeType === 'CircularArc' && typeof group.x === 'number') {
                  const center = sharedGeometryProcessor.transformPoint({ x: group.x, y: group.y, z: 0 }, matrix);
                  return { ...group, x: center.x, y: center.y };
                }

                // Handle Polyline edge format: points array
                if ((group.edgeType === 'Polyline' || group.edgeType === 'Line') && group.points) {
                  const transformedPoints = [];
                  if (group.bulge && group.NumberOfPolylineVertices) {
                    // Points with bulge: [x, y, bulge, x, y, bulge, ...]
                    for (let i = 0; i < group.NumberOfPolylineVertices; i++) {
                      const x = group.points[i * 3];
                      const y = group.points[i * 3 + 1];
                      const bulge = group.points[i * 3 + 2] || 0;
                      const transformed = sharedGeometryProcessor.transformPoint({ x, y, z: 0 }, matrix);
                      transformedPoints.push(transformed.x, transformed.y, bulge);
                    }
                  } else {
                    // Simple points: [x, y, x, y, ...]
                    for (let i = 0; i < group.points.length; i += 2) {
                      const transformed = sharedGeometryProcessor.transformPoint({ x: group.points[i], y: group.points[i + 1], z: 0 }, matrix);
                      transformedPoints.push(transformed.x, transformed.y);
                    }
                  }
                  return { ...group, points: transformedPoints };
                }

                // Legacy formats (lowercase)
                if (group.edgeType === 'polyline' && group.vertices) {
                  return {
                    ...group,
                    vertices: group.vertices.map(v => sharedGeometryProcessor.transformPoint(v, matrix))
                  };
                }
                if (group.edgeType === 'arc' && group.center) {
                  return {
                    ...group,
                    center: sharedGeometryProcessor.transformPoint(group.center, matrix)
                  };
                }
                if (group.edgeType === 'line' && group.start && group.end) {
                  return {
                    ...group,
                    start: sharedGeometryProcessor.transformPoint(group.start, matrix),
                    end: sharedGeometryProcessor.transformPoint(group.end, matrix)
                  };
                }
              }
              return group;
            })
          };
        });
      }

      const dxfBoundaryPaths = convertBoundaryPaths(transformedBoundaryPaths);

      if (dxfBoundaryPaths.length === 0) {
        console.warn('No valid boundaries found for hatch export');
        return;
      }

      const hatchPattern = createHatchFill(userData);
      const options = { ...getCommonDxfOptions(userData), elevation: 0 };

      // Store pattern data for post-processing (if pattern has patternLine from import)
      if (userData.pattern?.patternLine && userData.HatchPatternName) {
        if (!writer._arPatternData) {
          writer._arPatternData = new Map();
        }
        writer._arPatternData.set(userData.HatchPatternName, userData.pattern.patternLine);
      }

      writer.document.modelSpace.addHatch(dxfBoundaryPaths, hatchPattern, options);

    } catch (error) {
      console.error('Error exporting hatch:', error);
    }
  },

  /**
   * Processes a simple 'solidHatch' entity (from createSolidHatchFromPolyline)
   * @param {object} writer - The DXF writer instance.
   * @param {object} userData - Entity data containing points array.
   */
  solidhatch: (writer, userData) => {
    const { points, threeObject } = userData;

    if (!points?.length || points.length < MIN_BOUNDARY_POINTS) {
      console.warn('Skipping solidHatch with insufficient points');
      return;
    }

    try {
      // Apply transformation to points
      let transformedPoints = points;
      if (threeObject) {
        const { matrix } = sharedGeometryProcessor.getTransformation(threeObject);
        transformedPoints = points.map(p => sharedGeometryProcessor.transformPoint(p, matrix));
      }

      const hatchData = createSolidHatchFromPoints(transformedPoints);
      if (!hatchData) {
        console.warn('Failed to create solid hatch from points');
        return;
      }

      const options = getCommonDxfOptions(userData);
      writer.document.modelSpace.addHatch(hatchData.boundaryPaths, hatchData.fill, options);

    } catch (error) {
      console.error('Error exporting solid hatch:', error);
    }
  },

  /**
   * Mesh fill — ShapeGeometry / CircleGeometry solid dolgu (içi dolu tarama).
   * Panel sembollerinin (priz/anahtar/makine/fixture) solid hatch'leri Mesh olarak
   * tutuluyor; geometrinin her üçgenini ayrı solid HATCH olarak yazıp gerçek dolguyu
   * üretir (outline yerine). World transform threeObject üzerinden uygulanır.
   */
  meshfill: (writer, userData) => {
    const threeObject = userData.threeObject;
    const geom = threeObject?.geometry;
    const pos = geom?.attributes?.position;
    if (!pos || pos.count < 3) return;
    try {
      const { matrix } = sharedGeometryProcessor.getTransformation(threeObject);
      const options = getCommonDxfOptions(userData);

      // ── CircleGeometry (buat/nokta dolu disk) → TEK polyline-boundary solid HATCH ──
      // THREE.CircleGeometry vertex düzeni: index 0 = merkez, 1..N = çevre noktaları
      // (son nokta ilk ile çakışır). Çevreyi tek kapalı polyline boundary olarak yazıp
      // 64 ayrı üçgen yerine temiz tek bir solid hatch üretiriz. Her nokta world
      // matrix ile dönüştürülür → konum/açı/ölçek birebir korunur.
      if (geom.type === 'CircleGeometry') {
        const rim = [];
        for (let i = 1; i < pos.count - 1; i++) {
          rim.push(sharedGeometryProcessor.transformPoint({ x: pos.getX(i), y: pos.getY(i) }, matrix));
        }
        const hatchData = createSolidHatchFromPoints(rim);
        if (hatchData) {
          writer.document.modelSpace.addHatch(hatchData.boundaryPaths, hatchData.fill, options);
        }
        return;
      }

      // ── ShapeGeometry / PlaneGeometry / diğer üçgenlenmiş geometriler ──
      // Her üçgeni ayrı solid HATCH olarak yaz (keyfi şekiller + delikler için sağlam).
      const index = geom.index;
      const triCount = index ? Math.floor(index.count / 3) : Math.floor(pos.count / 3);
      for (let t = 0; t < triCount; t++) {
        const ia = index ? index.getX(t * 3)     : t * 3;
        const ib = index ? index.getX(t * 3 + 1) : t * 3 + 1;
        const ic = index ? index.getX(t * 3 + 2) : t * 3 + 2;
        const tri = [ia, ib, ic].map(i =>
          sharedGeometryProcessor.transformPoint({ x: pos.getX(i), y: pos.getY(i) }, matrix)
        );
        const hatchData = createSolidHatchFromPoints(tri);
        if (hatchData) {
          writer.document.modelSpace.addHatch(hatchData.boundaryPaths, hatchData.fill, options);
        }
      }
    } catch (error) {
      console.error('Error exporting mesh fill:', error);
    }
  },
};

export default entityHandlers; 